import maplibregl from "maplibre-gl";
import { memo, useEffect, useRef, useState } from "react";
import "maplibre-gl/dist/maplibre-gl.css";

import "./MyMap.css";

const MyMap = memo(
  ({
    zoom,
    setZoom,
    lng,
    setLng,
    lat,
    setLat,
    geojsonData,
    selectedRoadSegmentGeomMd5,
    setSelectedRoadSegmentGeomMd5,
    selectedAlertGeomMd5,
    setSelectedAlertGeomMd5,
    hoveredFeatureId,
    selectedFeatureId,
    backendUrl,
    backendToken,
    from,
    to,
    areAlertsVisible,
    jamsOrIrregularities,
    colorBy,
    roadNetwork,
    maxAngleDegrees,
    minOverlap,
    minCongestionCoefficient,
    congestionRateBinWidthMinutes,
    selectedDays,
    selectedHours,
  }) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const selectedAlertId = useRef(selectedAlertGeomMd5);
    const selectedRoadSegmentId = useRef(selectedRoadSegmentGeomMd5);
    const hoveredAlertId = useRef(null);
    const hoveredRoadSegmentId = useRef(null);
    const prevHoveredFeatureId = useRef(null);
    const prevSelectedFeatureId = useRef(null);
    const roadNetworkTilesPath = useRef(null);

    const [isMapLoaded, setIsMapLoaded] = useState(false);

    roadNetworkTilesPath.current =
      jamsOrIrregularities === "jams" ? "jams" : "irregularities";

    // Initialize the map
    useEffect(() => {
      if (map.current) {
        return; // stops map from initializing more than once
      }

      (async () => {
        const mapStyle = {
          version: 8,
          name: "Empty",
          metadata: {
            "mapbox:autocomposite": true,
            "mapbox:type": "template",
          },
          sources: {
            PLAN_SITG: {
              type: "raster",
              tiles: [
                "https://raster.sitg.ge.ch/arcgis/rest/services/PLAN_SITG_EPSG2056/MapServer/export?bbox={bbox-epsg-3857}&size=512,512&bboxSR=3857&&imageSR=3857&format=png&transparent=false&dpi=96&f=image&layers=",
              ],
              tileSize: 512,
              attribution:
                "<a href='https://ge.ch/sitg/fiche/7206'>Plan SITG</a>",
            },
            geojsonData: {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: [],
              },
              promoteId: "geometry_md5",
            },
            roadNetworkWithCounts: {
              type: "vector",
              tiles: [
                `${backendUrl}/tiles/${roadNetworkTilesPath.current}/{z}/{x}/{y}.pbf?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&color_by=${encodeURIComponent(colorBy)}&road_network=${encodeURIComponent(roadNetwork)}&max_angle_degrees=${encodeURIComponent(maxAngleDegrees)}&min_overlap=${encodeURIComponent(minOverlap)}&min_congestion_coefficient=${encodeURIComponent(minCongestionCoefficient)}&bin_width_minutes=${encodeURIComponent(congestionRateBinWidthMinutes)}&days=${encodeURIComponent(selectedDays)}&hours=${encodeURIComponent(selectedHours)}`,
              ],
              // promoteId: { "road_network": "geometry_md5" }
              promoteId: "geometry_md5",
            },
            alerts: {
              type: "vector",
              tiles: [
                `${backendUrl}/tiles/alerts/{z}/{x}/{y}.pbf?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&days=${encodeURIComponent(selectedDays)}&hours=${encodeURIComponent(selectedHours)}`,
              ],
              promoteId: "geometry_md5",
            },
          },
          layers: [
            {
              id: "PLAN_SITG",
              type: "raster",
              source: "PLAN_SITG",
              layout: {
                visibility: "visible",
              },
              paint: {
                "raster-saturation": -0.8,
              },
            },
          ],
        };

        map.current = new maplibregl.Map({
          container: mapContainer.current,
          style: mapStyle,
          center: [lng, lat],
          zoom: zoom,
          maxZoom: 20,
          transformRequest: (url, resourceType) => {
            // authenticated access to MVT tiles
            if (
              resourceType === "Tile" &&
              url.startsWith(`${backendUrl}/tiles/`)
            ) {
              return {
                url,
                headers: {
                  Authorization: `Bearer ${backendToken}`,
                },
              };
            }
          },
        });

        map.current.addControl(
          new maplibregl.NavigationControl(),
          "bottom-right",
        );

        map.current.on("load", async () => {
          // must credit the author:
          // <a href="https://www.flaticon.com/free-icons/next" title="next icons">Next icons created by Roundicons - Flaticon</a>
          // <a href="https://www.flaticon.com/free-icons/risk" title="risk icons">Risk icons created by Freepik - Flaticon</a>
          // <a href="https://www.flaticon.com/free-icons/close" title="close icons">Close icons created by ariefstudio - Flaticon</a>
          // const image = await map.current.loadImage('https://upload.wikimedia.org/wikipedia/commons/7/7c/201408_cat.png');
          const arrowImg = await map.current.loadImage("small-right-arrow.png");
          map.current.addImage("arrow", arrowImg.data, { sdf: true });

          const crossImg = await map.current.loadImage("cross_64px.png");
          map.current.addImage("cross", crossImg.data, { sdf: true });

          const accidentImg = await map.current.loadImage(
            "car-accident_64px.png",
          );
          map.current.addImage("accident", accidentImg.data, { sdf: false });

          const jamImg = await map.current.loadImage("fleet_64px.png");
          map.current.addImage("jam", jamImg.data, { sdf: false });

          const weatherHazard =
            await map.current.loadImage("hazardous_64px.png");
          map.current.addImage("weather-hazard", weatherHazard.data, {
            sdf: false,
          });

          const roadClosed = await map.current.loadImage("close_64px.png");
          map.current.addImage("road-closed", roadClosed.data, { sdf: false });

          const questionMark = await map.current.loadImage(
            "question-mark_64px.png",
          );
          map.current.addImage("question-mark", questionMark.data, {
            sdf: false,
          });

          const sign = await map.current.loadImage("sign_64px.png");
          map.current.addImage("sign", sign.data, {
            sdf: false,
          });

          setIsMapLoaded(true);
        });

        map.current.on("zoomend", async () => {
          setZoom(map.current.getZoom()); // this goes upstream to the parent component
        });

        map.current.on("moveend", async () => {
          const center = map.current.getCenter();
          setLng(center.lng); // this goes upstream to the parent component
          setLat(center.lat); // this goes upstream to the parent component
        });
      })();
    }, [
      zoom,
      setZoom,
      lng,
      setLng,
      lat,
      setLat,
      backendUrl,
      backendToken,
      from,
      to,
      colorBy,
      roadNetwork,
      maxAngleDegrees,
      minOverlap,
      minCongestionCoefficient,
      congestionRateBinWidthMinutes,
      selectedDays,
      selectedHours,
    ]);

    // add `road-network-with-counts` layer
    useEffect(() => {
      if (map.current && isMapLoaded) {
        if (
          typeof map.current.getLayer("road-network-with-counts") ===
          "undefined"
        ) {
          const roadNetworkStyle = {
            paint: {
              "line-color": [
                // see https://maplibre.org/maplibre-style-spec/expressions/#data-expressions
                "interpolate-hcl",
                ["linear"],
                ["get", "normalized_cnt"],
                // see https://gradient.page/ui-gradients/rastafari
                // Note: if changing this, make sure to change the legend colors accordingly (in MapSettingsLegend.css)
                0,
                "#1E9600",
                0.5,
                "#FFF200",
                1,
                "#FF0000",
              ],
              "line-width": [
                "case",
                [
                  "boolean",
                  [
                    "any",
                    ["boolean", ["feature-state", "hovered"], false],
                    ["boolean", ["feature-state", "selected"], false],
                  ],
                  false,
                ],
                8,
                3,
              ],
              "line-opacity": 0.8,
            },
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
          };
          map.current.addLayer({
            id: "road-network-with-counts",
            type: "line",
            source: "roadNetworkWithCounts",
            "source-layer": "road_network",
            ...roadNetworkStyle,
          });
        }

        if (selectedRoadSegmentId.current) {
          map.current.setFeatureState(
            {
              source: "roadNetworkWithCounts",
              sourceLayer: "road_network",
              id: selectedRoadSegmentId.current,
            },
            { selected: true },
          );
        }
      }
    }, [isMapLoaded]);

    // add `closed-roads` and `closed-roads-symbols` layers
    useEffect(() => {
      if (map.current && isMapLoaded) {
        if (typeof map.current.getLayer("closed-roads") === "undefined") {
          const closedRoadsStyle = {
            paint: {
              "line-color": "black",
              "line-width": 2,
              "line-opacity": 0.8,
              // 'line-dasharray': [2, 2]
            },
            layout: {
              "line-join": "round",
              "line-cap": "square",
            },
          };
          map.current.addLayer({
            id: "closed-roads",
            type: "line",
            source: "roadNetworkWithCounts",
            "source-layer": "closed_roads",
            ...closedRoadsStyle,
          });
        }

        if (
          typeof map.current.getLayer("closed-roads-symbols") === "undefined"
        ) {
          map.current.addLayer({
            id: "closed-roads-symbols",
            type: "symbol",
            source: "roadNetworkWithCounts",
            "source-layer": "closed_roads",
            layout: {
              "symbol-placement": "line",
              // 'symbol-spacing': 50,
              "symbol-avoid-edges": true,
              "icon-allow-overlap": false,
              // 'icon-ignore-placement': true,
              "icon-rotate": 0,
              "icon-rotation-alignment": "viewport",
              "icon-image": "cross",
              "icon-size": 0.25,
              visibility: "visible",
            },
            paint: {
              "icon-color": "rgb(255, 0, 0)",
              // "icon-halo-color": "rgb(0, 255, 0)",
              // "icon-halo-width": 10
            },
            // minzoom: 10,
          });
        }
      }
    }, [isMapLoaded]);

    // add `alerts` and `selected-alert` layers
    useEffect(() => {
      if (map.current && isMapLoaded) {
        if (typeof map.current.getLayer("alerts") === "undefined") {
          map.current.addLayer({
            id: "alerts",
            type: "symbol",
            source: "alerts",
            "source-layer": "alerts",
            layout: {
              "symbol-placement": "point",
              "icon-allow-overlap": true,
              "icon-rotate": 0,
              "icon-image": [
                "match",
                ["get", "type"],
                "ACCIDENT",
                "accident",
                //
                "JAM",
                "jam",
                //
                "ROAD_CLOSED",
                "road-closed",
                //
                "HAZARD",
                "sign",
                //
                "WEATHERHAZARD",
                "weather-hazard",
                //
                "question-mark",
              ],
              "icon-size": 0.5,
              visibility: areAlertsVisible ? "visible" : "none",
            },
            paint: {
              "icon-opacity": [
                "case",
                ["boolean", ["feature-state", "hovered"], false],
                0.7,
                1,
              ],
            },
          });
        }

        if (typeof map.current.getLayer("selected-alert") === "undefined") {
          map.current.addLayer({
            id: "selected-alert",
            type: "circle",
            source: "alerts",
            "source-layer": "alerts",
            layout: {
              visibility: areAlertsVisible ? "visible" : "none",
            },
            paint: {
              "circle-radius": 20,
              "circle-opacity": 0,
              "circle-stroke-width": 5,
              "circle-stroke-color": "orange",
              "circle-stroke-opacity": [
                "case",
                ["boolean", ["feature-state", "selected"], false],
                1,
                0,
              ],
            },
          });

          if (selectedAlertId.current) {
            map.current.setFeatureState(
              {
                source: "alerts",
                sourceLayer: "alerts",
                id: selectedAlertId.current,
              },
              { selected: true },
            );
          }
        }
      }
    }, [isMapLoaded, areAlertsVisible]);

    // add `geojson-data` and `geojson-data-direction` layers
    useEffect(() => {
      if (map.current && isMapLoaded) {
        if (typeof map.current.getLayer("geojson-data") === "undefined") {
          map.current.addLayer({
            id: "geojson-data",
            type: "line",
            source: "geojsonData",
            layout: {
              visibility: "visible",
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "blue",
              "line-width": [
                "case",
                ["boolean", ["feature-state", "hovered"], false],
                8,
                3,
              ],
              "line-opacity": [
                "case",
                [
                  "any",
                  ["boolean", ["feature-state", "hovered"], false],
                  ["boolean", ["feature-state", "selected"], false],
                ],
                1,
                0,
              ],
            },
          });

          if (selectedFeatureId) {
            map.current.setFeatureState(
              {
                source: "geojsonData",
                id: selectedFeatureId,
              },
              { selected: true },
            );
          }
        }

        if (
          typeof map.current.getLayer("geojson-data-direction") === "undefined"
        ) {
          map.current.addLayer({
            id: "geojson-data-direction",
            type: "symbol",
            source: "geojsonData",
            layout: {
              "symbol-placement": "line",
              "symbol-spacing": 50,
              "symbol-avoid-edges": false,
              "icon-allow-overlap": true,
              // 'icon-ignore-placement': true,
              "icon-rotate": 0,
              "icon-image": "arrow",
              "icon-size": 1,
              visibility: "visible",
            },
            paint: {
              "icon-color": "blue",
              // "icon-halo-color": "rgb(0, 255, 0)",
              // "icon-halo-width": 10
              "icon-opacity": [
                "case",
                [
                  "any",
                  ["boolean", ["feature-state", "hovered"], false],
                  ["boolean", ["feature-state", "selected"], false],
                ],
                1,
                0,
              ],
            },
            minzoom: 10,
          });
        }
      }
    }, [isMapLoaded, selectedFeatureId]);

    // setup mouse interactions with layers
    useEffect(() => {
      if (map.current && isMapLoaded) {
        if (map.current.getLayer("road-network-with-counts")) {
          map.current.on("mouseenter", "road-network-with-counts", (e) => {
            map.current.getCanvas().style.cursor = "crosshair";
            if (e.features.length > 0) {
              if (hoveredRoadSegmentId.current) {
                map.current.setFeatureState(
                  {
                    source: "roadNetworkWithCounts",
                    sourceLayer: "road_network",
                    id: hoveredRoadSegmentId.current,
                  },
                  { hovered: false },
                );
              }
              hoveredRoadSegmentId.current = e.features[0].id;
              map.current.setFeatureState(
                {
                  source: "roadNetworkWithCounts",
                  sourceLayer: "road_network",
                  id: hoveredRoadSegmentId.current,
                },
                { hovered: true },
              );
            }
          });

          map.current.on("mouseleave", "road-network-with-counts", () => {
            map.current.getCanvas().style.cursor = "";
            if (hoveredRoadSegmentId.current) {
              map.current.setFeatureState(
                {
                  source: "roadNetworkWithCounts",
                  sourceLayer: "road_network",
                  id: hoveredRoadSegmentId.current,
                },
                { hovered: false },
              );
            }
            hoveredRoadSegmentId.current = null;
          });

          map.current.on("click", "road-network-with-counts", (e) => {
            // for debugging purposes ---------------------------------------
            // console.log(e.features[0].properties.normalized_cnt);
            // --------------------------------------------------------------

            setSelectedRoadSegmentGeomMd5(
              e.features[0].properties.geometry_md5,
            );
            setSelectedAlertGeomMd5(null);
            if (selectedAlertId.current) {
              map.current.setFeatureState(
                {
                  source: "alerts",
                  sourceLayer: "alerts",
                  id: selectedAlertId.current,
                },
                { selected: false },
              );
            }

            if (selectedRoadSegmentId.current) {
              map.current.setFeatureState(
                {
                  source: "roadNetworkWithCounts",
                  sourceLayer: "road_network",
                  id: selectedRoadSegmentId.current,
                },
                { selected: false },
              );
            }

            selectedRoadSegmentId.current = e.features[0].id;
            map.current.setFeatureState(
              {
                source: "roadNetworkWithCounts",
                sourceLayer: "road_network",
                id: selectedRoadSegmentId.current,
              },
              { selected: true },
            );
          });
        }

        if (map.current.getLayer("alerts")) {
          map.current.on("mousemove", "alerts", (e) => {
            map.current.getCanvas().style.cursor = "crosshair";
            if (e.features.length > 0) {
              if (hoveredAlertId.current) {
                map.current.setFeatureState(
                  {
                    source: "alerts",
                    sourceLayer: "alerts",
                    id: hoveredAlertId.current,
                  },
                  { hovered: false },
                );
              }
              hoveredAlertId.current = e.features[0].id;
              map.current.setFeatureState(
                {
                  source: "alerts",
                  sourceLayer: "alerts",
                  id: hoveredAlertId.current,
                },
                { hovered: true },
              );
            }
          });

          map.current.on("mouseleave", "alerts", () => {
            map.current.getCanvas().style.cursor = "";
            if (hoveredAlertId.current) {
              map.current.setFeatureState(
                {
                  source: "alerts",
                  sourceLayer: "alerts",
                  id: hoveredAlertId.current,
                },
                { hovered: false },
              );
            }
            hoveredAlertId.current = null;
          });

          map.current.on("click", "alerts", (e) => {
            setSelectedAlertGeomMd5(e.features[0].properties.geometry_md5);
            setSelectedRoadSegmentGeomMd5(null);

            map.current.setFeatureState(
              {
                source: "roadNetworkWithCounts",
                sourceLayer: "road_network",
                id: selectedRoadSegmentId.current,
              },
              { selected: false },
            );

            map.current.setFeatureState(
              {
                source: "alerts",
                sourceLayer: "alerts",
                id: selectedAlertId.current,
              },
              { selected: false },
            );

            selectedAlertId.current = e.features[0].id;
            map.current.setFeatureState(
              {
                source: "alerts",
                sourceLayer: "alerts",
                id: selectedAlertId.current,
              },
              { selected: true },
            );
          });
        }
      }
    }, [isMapLoaded, setSelectedAlertGeomMd5, setSelectedRoadSegmentGeomMd5]);

    // Update geojsonData source
    useEffect(() => {
      if (map.current && isMapLoaded) {
        if (map.current.getLayer("geojson-data")) {
          if (geojsonData?.features) {
            map.current.getSource("geojsonData").setData(geojsonData);
          } else {
            map.current.getSource("geojsonData").setData({
              type: "FeatureCollection",
              features: [],
            });
          }
        }
      }
    }, [isMapLoaded, geojsonData]);

    // Highlight the jam/irregularity feature which is being hovered on the plot
    useEffect(() => {
      if (map.current) {
        if (geojsonData?.features && hoveredFeatureId !== undefined) {
          map.current.setFeatureState(
            { source: "geojsonData", id: hoveredFeatureId },
            { hovered: true },
          );
          map.current.setFeatureState(
            { source: "geojsonData", id: prevHoveredFeatureId.current },
            { hovered: false },
          );
        }
      }

      prevHoveredFeatureId.current = hoveredFeatureId;
    }, [geojsonData, hoveredFeatureId]);

    // Highlight the jam/irregularity feature which has been clicked on the plot
    useEffect(() => {
      if (map.current) {
        if (geojsonData?.features && selectedFeatureId !== undefined) {
          map.current.setFeatureState(
            { source: "geojsonData", id: selectedFeatureId },
            { selected: true },
          );
          map.current.setFeatureState(
            { source: "geojsonData", id: prevSelectedFeatureId.current },
            { selected: false },
          );
        }
      }

      prevSelectedFeatureId.current = selectedFeatureId;
    }, [geojsonData, selectedFeatureId]);

    // Display/hide alerts
    useEffect(() => {
      if (map.current) {
        if (map.current.getLayer("alerts")) {
          if (
            selectedDays.length === 0 ||
            selectedHours.length === 0 ||
            !areAlertsVisible
          ) {
            map.current.setLayoutProperty("alerts", "visibility", "none");
            map.current.setLayoutProperty(
              "selected-alert",
              "visibility",
              "none",
            );
          } else {
            map.current
              .getSource("alerts")
              .setTiles([
                `${backendUrl}/tiles/alerts/{z}/{x}/{y}.pbf?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&days=${encodeURIComponent(selectedDays)}&hours=${encodeURIComponent(selectedHours)}`,
              ]);
            map.current.setLayoutProperty("alerts", "visibility", "visible");
            map.current.setLayoutProperty(
              "selected-alert",
              "visibility",
              "visible",
            );
          }
        }
      }
    }, [areAlertsVisible, backendUrl, from, to, selectedDays, selectedHours]);

    // Switch jams <-> irregularities
    useEffect(() => {
      if (map.current) {
        if (map.current.getLayer("road-network-with-counts")) {
          roadNetworkTilesPath.current =
            jamsOrIrregularities === "jams" ? "jams" : "irregularities";

          if (selectedDays.length === 0 || selectedHours.length === 0) {
            map.current.setLayoutProperty(
              "road-network-with-counts",
              "visibility",
              "none",
            );
            map.current.setLayoutProperty("closed-roads", "visibility", "none");
            map.current.setLayoutProperty(
              "closed-roads-symbols",
              "visibility",
              "none",
            );
          } else {
            map.current
              .getSource("roadNetworkWithCounts")
              .setTiles([
                `${backendUrl}/tiles/${roadNetworkTilesPath.current}/{z}/{x}/{y}.pbf?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&color_by=${encodeURIComponent(colorBy)}&road_network=${encodeURIComponent(roadNetwork)}&max_angle_degrees=${encodeURIComponent(maxAngleDegrees)}&min_overlap=${encodeURIComponent(minOverlap)}&min_congestion_coefficient=${encodeURIComponent(minCongestionCoefficient)}&bin_width_minutes=${encodeURIComponent(congestionRateBinWidthMinutes)}&days=${encodeURIComponent(selectedDays)}&hours=${encodeURIComponent(selectedHours)}`,
              ]);
            map.current.setLayoutProperty(
              "road-network-with-counts",
              "visibility",
              "visible",
            );
            map.current.setLayoutProperty(
              "closed-roads",
              "visibility",
              "visible",
            );
            map.current.setLayoutProperty(
              "closed-roads-symbols",
              "visibility",
              "visible",
            );
          }
        }
      }
    }, [
      jamsOrIrregularities,
      backendUrl,
      from,
      to,
      colorBy,
      roadNetwork,
      maxAngleDegrees,
      minOverlap,
      minCongestionCoefficient,
      congestionRateBinWidthMinutes,
      selectedDays,
      selectedHours,
    ]);

    return (
      <div className="map-wrap">
        <div ref={mapContainer} className="map" />
      </div>
    );
  },
);

export default MyMap;
